import type React from 'react';

import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';

import type { TownsquareApp } from '../store/context';

import { useGetCrossProductWrappedUrl } from './useGetCrossProductWrappedUrl';

type UserProfileLinkResult = {
	href: string;
	target: string | undefined;
	onNavigate: ((event: React.MouseEvent<HTMLAnchorElement>) => void) | undefined;
	isCrossAppMauGateEnabled: boolean;
};

const shouldOpenInSameTab = (townsquareApp: TownsquareApp | undefined): boolean =>
	townsquareApp === 'teams' || townsquareApp === 'teams-isolated';

export const useUserProfileLink = (
	userId: string,
	cloudId: string,
	townsquareApp?: TownsquareApp,
): UserProfileLinkResult => {
	const {
		href: rawHref,
		target,
		onNavigate,
	} = navigateToTeamsApp({
		type: 'USER',
		payload: { userId },
		cloudId,
		shouldOpenInSameTab: shouldOpenInSameTab(townsquareApp),
	});

	// When no townsquareApp is provided, fall back to `'teams'` for the bridge string so
	// the analytics tag is always a real app name, never the literal `"undefined-customLink"`.
	const bridgeApp = townsquareApp ?? 'teams';
	const wrappedHref = useGetCrossProductWrappedUrl(
		`${bridgeApp}-customLink`,
		rawHref,
		townsquareApp,
	);

	const href = townsquareApp ? wrappedHref : rawHref;
	const isCrossAppMauGateEnabled = Boolean(townsquareApp) && href !== rawHref;

	return { href, target, onNavigate, isCrossAppMauGateEnabled };
};
